/**
 * MODULE: _shared/frontend-shell/src/hooks/billing/useCheckout.ts
 * PURPOSE: Stripe checkout — Brazn Pro subscription or redirect to AI usage top-up
 * RECENT: 2026-06-12 — Uses auth subscription API (replaces dead /api/v1/billing/checkout)
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { getErrorMessage } from '@shared/lib/frontend/api/errors';

const API = `${getApiBaseUrl()}/api/v1/auth/subscription`;

export interface CheckoutPayload {
  planId: string;
  name: string;
  email: string;
  company?: string;
  country: string;
  vatId?: string;
}

export interface CheckoutResponse {
  url: string;
  sessionId?: string;
}

async function processCheckout(payload: CheckoutPayload): Promise<CheckoutResponse> {
  const token = localStorage.getItem('access_token');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const tenantId = localStorage.getItem('tenant_id')?.replace(/^tnt_/, '');

  const response = await fetch(`${API}/create-checkout-session`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      plan_tier: 'brazn_pro',
      email: payload.email,
      success_url: `${origin}/payment-confirm?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/subscription-setup`,
      tenant_id: tenantId,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Checkout failed' }));
    throw new Error(error.detail || error.message || 'Checkout failed');
  }

  return response.json();
}

export function useCheckout() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkout = async (payload: CheckoutPayload) => {
    setLoading(true);
    setError(null);

    try {
      const data = await processCheckout(payload);
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Checkout failed'));
      setLoading(false);
    }
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
  };

  return {
    checkout,
    loading,
    error,
    invalidate,
    mutateAsync: checkout,
    isPending: loading,
  };
}
